// Device-linking page — CLOUD-ONLY. Two modes, one per initiation direction:
//
// 1. DESKTOP-INITIATED (RFC 8252 loopback, same machine): a desktop OneStone
//    app on THIS computer starts an ephemeral 127.0.0.1:<port> listener and
//    opens the system browser here at /link?port=<port>&state=<state>
//    (optionally &name=<label>). The signed-in user approves; the page mints a
//    single-use one-time-token and redirects to the loopback callback carrying
//    it + the desktop's opaque `state`. The redirect host is HARDCODED to
//    127.0.0.1 (never an open redirect); consent is explicit.
//
// 2. PHONE-INITIATED (RFC 10027 trusted-device code, cross-device): /link with
//    NO desktop params. The signed-in user taps "Get a code" — the SERVER mints
//    a one-time-token bound to this session and stores it under an 8-letter
//    code (60s TTL, single-use); only the code reaches this browser. The user
//    types the code into their desktop app (Connections ▸ Cloud Sync), whose
//    LOCAL SERVER claims it. This page polls claim status and confirms with
//    the desktop's name (§6.1.12 out-of-band confirmation). Initiation is
//    restricted to the authenticated session (§6.1.7 trusted devices), which is
//    what keeps this free of the RFC 8628 device-code phishing class — an
//    attacker cannot mint codes to phish a victim into approving.
//
// Design register mirrors CloudLogin (frontend-design "System UI"): one
// centered card, ONE accent on the primary action, @onestone/ui primitives,
// aria-live status/error regions, ≥h-10 targets.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@onestone/ui/button';

type Phase = 'confirm' | 'working' | 'redirecting' | 'cancelled' | 'invalid' | 'error';

/** Parse + validate a loopback port: an integer in [1, 65535]. */
function parsePort(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 65535 ? n : null;
}

/** Shared page chrome — header + card shell used by both modes. */
function Shell({ children, footer }: { children: React.ReactNode; footer?: string }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src="/logo-256.png" alt="" className="h-10 w-10 rounded-lg shadow-sm" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Link a device</h1>
            <p className="text-sm text-muted-foreground">Connect a desktop app to your OneStone account.</p>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">{children}</div>
        <p className="mt-6 text-center text-xs text-muted-foreground/80">
          {footer ?? 'You’re signed in — linking applies to this OneStone account only.'}
        </p>
      </div>
    </main>
  );
}

/** Format an 8-char code for display: BCDF-GHJK. */
function formatCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

type MintPhase = 'idle' | 'minting' | 'code' | 'claimed' | 'expired' | 'error';
const STATUS_POLL_MS = 2000;

/** Phone-initiated mode: mint a code here, type it on the desktop. */
function MintCode() {
  const [phase, setPhase] = useState<MintPhase>('idle');
  const [code, setCode] = useState('');
  const [id, setId] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [deviceName, setDeviceName] = useState('');

  const getCode = useCallback(async () => {
    setPhase('minting');
    try {
      const res = await fetch('/api/link-code/start', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setPhase('error');
        return;
      }
      const data = (await res.json()) as { code?: string; id?: string; expires_in?: number };
      if (!data.code || !data.id) {
        setPhase('error');
        return;
      }
      setCode(data.code);
      setId(data.id);
      setExpiresAt(Date.now() + (data.expires_in ?? 60) * 1000);
      setSecondsLeft(data.expires_in ?? 60);
      setPhase('code');
    } catch {
      setPhase('error');
    }
  }, []);

  // While a code is live: tick the countdown and poll for the desktop's claim.
  useEffect(() => {
    if (phase !== 'code') return;
    const tick = setInterval(() => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) setPhase('expired');
    }, 500);
    const poll = setInterval(() => {
      void (async () => {
        try {
          const res = await fetch(`/api/link-code/status/${id}`, { credentials: 'include' });
          if (!res.ok) return;
          const status = (await res.json()) as { claimed?: boolean; deviceName?: string };
          if (status.claimed) {
            setDeviceName(status.deviceName ?? '');
            setPhase('claimed');
          }
        } catch {
          // transient poll failure — the next interval retries
        }
      })();
    }, STATUS_POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [phase, id, expiresAt]);

  if (phase === 'claimed') {
    return (
      <div className="flex flex-col items-center gap-4 py-4 text-center" aria-live="polite">
        <div className="space-y-1">
          <p className="text-sm font-medium">Desktop linked</p>
          <p className="text-sm text-muted-foreground">
            {deviceName
              ? `“${deviceName}” is now connected to your account.`
              : 'Your desktop is now connected to your account.'}
          </p>
        </div>
        {/* Terminal state needs a way BACK — without this the page dead-ends
            after linking (operator-reported UX gap, 2026-09-24). */}
        <a
          href="/"
          className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Back to the app
        </a>
      </div>
    );
  }

  if (phase === 'code') {
    return (
      <div className="flex flex-col gap-4" aria-live="polite">
        <div className="space-y-1 text-center">
          <p className="text-sm font-medium">Enter this code on your desktop</p>
          <p className="text-sm text-muted-foreground">
            In the desktop app, open Connections ▸ Cloud Sync and type the code.
          </p>
        </div>
        <p className="select-all text-center font-mono text-3xl font-semibold tracking-[0.2em]">{formatCode(code)}</p>
        <p className="text-center text-xs text-muted-foreground">
          Expires in {secondsLeft}s · works once · never share it with anyone else
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1 text-center">
        <p className="text-sm font-medium">Link a desktop with a code</p>
        <p className="text-sm text-muted-foreground">
          Get a one-time code here, then enter it in your desktop app under Connections ▸ Cloud Sync.
        </p>
      </div>
      {phase === 'expired' && (
        <p className="text-center text-xs text-muted-foreground" aria-live="polite">
          That code expired. Get a new one when your desktop is ready.
        </p>
      )}
      {phase === 'error' && (
        <p className="text-center text-xs text-destructive" aria-live="assertive">
          Could not get a code. Make sure you are signed in, then try again.
        </p>
      )}
      <Button type="button" onClick={() => void getCode()} disabled={phase === 'minting'} className="h-10 w-full">
        {phase === 'minting' ? 'Getting a code…' : 'Get a code'}
      </Button>
    </div>
  );
}

export function LinkDevice() {
  const [params] = useSearchParams();
  const port = useMemo(() => parsePort(params.get('port')), [params]);
  const state = params.get('state') ?? '';
  const deviceName = (params.get('name') ?? '').slice(0, 60);
  // Params PRESENT but malformed → invalid (a desktop sent us something broken).
  // Params ABSENT entirely → phone-initiated mode (the user navigated here).
  const hasDesktopParams = params.get('port') !== null || params.get('state') !== null;
  const paramsValid = port !== null && state.length > 0;

  const [phase, setPhase] = useState<Phase>(paramsValid ? 'confirm' : 'invalid');
  const [error, setError] = useState('');

  const approve = useCallback(async () => {
    if (port === null || !state) {
      setPhase('invalid');
      return;
    }
    setError('');
    setPhase('working');
    try {
      // Session-gated GET — the cookie mints a single-use token bound to this
      // signed-in session (better-auth one-time-token plugin).
      const res = await fetch('/api/auth/one-time-token/generate', { credentials: 'include' });
      if (!res.ok) {
        setError('Could not start linking. Make sure you are signed in, then try again.');
        setPhase('confirm');
        return;
      }
      const data = (await res.json()) as { token?: string };
      if (!data.token) {
        setError('The server did not return a link token. Try again.');
        setPhase('confirm');
        return;
      }
      // Host is hardcoded to loopback; only the validated numeric port varies.
      const callback = `http://127.0.0.1:${port}/callback?token=${encodeURIComponent(
        data.token,
      )}&state=${encodeURIComponent(state)}`;
      setPhase('redirecting');
      window.location.assign(callback);
    } catch {
      setError('Network error. Check your connection and try again.');
      setPhase('confirm');
    }
  }, [port, state]);

  // Phone-initiated mode — no desktop params at all.
  if (!hasDesktopParams) {
    return (
      <Shell footer="Anyone with a live code can link a desktop to your account — codes expire in 60 seconds.">
        <MintCode />
      </Shell>
    );
  }

  return (
    <Shell>
      {phase === 'working' || phase === 'redirecting' ? (
        <div className="flex flex-col items-center gap-3 py-8" aria-busy="true" aria-live="polite">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground motion-reduce:animate-none" />
          <span className="text-sm text-muted-foreground">
            {phase === 'redirecting' ? 'Returning to your app…' : 'Preparing the link…'}
          </span>
        </div>
      ) : phase === 'confirm' ? (
        <div className="flex flex-col gap-4">
          <div className="space-y-1 text-center">
            <p className="text-sm font-medium">
              {deviceName ? `“${deviceName}” wants to connect` : 'A desktop app wants to connect'}
            </p>
            <p className="text-sm text-muted-foreground">
              Linking lets a OneStone app on this computer sync your data with your account.
            </p>
          </div>

          <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-muted-foreground">
            Only continue if <span className="font-medium text-foreground">you</span> just started linking from a
            OneStone app on <span className="font-medium text-foreground">this computer</span>. If you did not, close
            this page.
          </p>

          <div className="flex flex-col gap-2">
            <Button type="button" onClick={() => void approve()} className="h-10 w-full">
              Connect this device
            </Button>
            <Button type="button" variant="outline" onClick={() => setPhase('cancelled')} className="h-10 w-full">
              Cancel
            </Button>
          </div>
        </div>
      ) : phase === 'cancelled' ? (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <div className="space-y-1">
            <p className="text-sm font-medium">Linking cancelled</p>
            <p className="text-sm text-muted-foreground">No device was connected. You can close this page.</p>
          </div>
        </div>
      ) : phase === 'invalid' ? (
        <div className="flex flex-col gap-2 py-2 text-center">
          <p className="text-sm font-medium">This link isn&rsquo;t valid</p>
          <p className="text-sm text-muted-foreground">
            Start linking from your OneStone desktop app — it opens this page with the details it needs.
          </p>
        </div>
      ) : (
        // error
        <div className="flex flex-col gap-2 py-2 text-center">
          <p className="text-sm font-medium">Something went wrong</p>
          <p className="text-sm text-muted-foreground">Return to your app and start linking again.</p>
        </div>
      )}

      {error ? (
        <p className="mt-3 text-center text-xs text-destructive" aria-live="assertive">
          {error}
        </p>
      ) : null}
    </Shell>
  );
}
